import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { openModal } from "risa-oasis-ui_v2";
import InfoGrid from "../../components/infoGrid/infoGrid";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import ApproveEvbv from "../../components/modals/approveEvbv/approveEvbv";
import ImageviewerModal from "../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerTimestamp from "../../components/modals/imageViewerModal/imageViewerTimestamp";
import SingleTab from "../../components/singleTab/singleTab";
import { InfoGridData } from "../../data-model/infoGrid";
import { LocalStorageKeys } from "../../enums/localStorageKeys";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import {
  financialStatusOptions,
  MedicalPaOrderStatus,
} from "../../enums/medicalPaOrderStatus";
import { fetchAuditTrialOverview } from "../../redux/slice/auditTrialOverviewSlice";
import { getMedicalPaOrderDetailsFor } from "../../redux/slice/medicalPa/singleMedicalPaOrder";
import { AppDispatch, RootState } from "../../redux/store/store";
import BackArrowBlue from "../../svg/back-arrow-blue";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { getAuthStatusOfMedicalPaOrder } from "../../utils/getAuthStatusOfMedicalPaOrder";
import { rotateLastSegmentFirst } from "../../utils/getFullNameOfTheProvider";
import { getItemFromLocalStorage } from "../../utils/localStorageHelper";
import { capitalizeString } from "../../utils/stringModifications";
import Header from "../singleOrderLayout/components/header/header";
import {
  generatePatientDetails,
  generateProviderDetails,
} from "../worklists/singleOrder/utils/singleOrderPageDataGenerator";
import { determineFinalTabsListToBeShown } from "./utils/tabSwitchElementsGenerator";

const SingleOrderForExternalLayout = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { singleMedicalPaOrder, isLoading, error, unsubscribe } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const [isAutoNavigated, setIsAutoNavigated] = useState(false);
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const [isApproveEvbvModalOpen, setIsApproveEvbvModalOpen] =
    React.useState<boolean>(false);
  const [imageViewerModalOpen, setImageViewerModalOpen] = useState(false);

  useEffect(() => {
    if (isApproveEvbvModalOpen) {
      openModal("approve-evbv-modal");
    }
  }, [isApproveEvbvModalOpen]);

  const [patientData, setPatientData] = useState<InfoGridData[] | null>(null);
  const [providerData, setProviderData] = useState<InfoGridData[] | null>(null);

  useEffect(() => {
    if (orderId) {
      dispatch(getMedicalPaOrderDetailsFor(orderId));
      dispatch(fetchAuditTrialOverview(orderId));
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [orderId]);

  useEffect(() => {
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [unsubscribe]);

  const checkIfSubmissionPageShouldBeShown = () => {
    const getAuthStatus = getAuthStatusOfMedicalPaOrder(
      singleMedicalPaOrder ?? {},
      authStatusOptions ?? [],
    ).id;

    if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthOnFile) {
      return false;
    }

    if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthRequired) {
      if (
        singleMedicalPaOrder?.status?.submission === undefined ||
        singleMedicalPaOrder?.status?.submission === null ||
        singleMedicalPaOrder?.status?.submission ===
          MedicalPaOrderStatus.PAYER_NOT_SUPPORTED
      ) {
        return false;
      }
      return true;
    }

    return false;
  };

  const getCurrentStatusBadge = useMemo(() => {
    const currentStatus =
      singleMedicalPaOrder?.status?.financial_review ??
      MedicalPaOrderStatus.NEW;
    const currentStatusBadge = financialStatusOptions.find(
      (option) => option.id === currentStatus,
    );
    return currentStatusBadge;
  }, [singleMedicalPaOrder]);

  const checkIfInfoGridHeadersHouldBeShown = (data: InfoGridData[]) => {
    const isHeaderPresent = data.every((item) => item.text !== "");
    return isHeaderPresent;
  };

  useEffect(() => {
    if (singleMedicalPaOrder && !isLoading && !error) {
      setPatientData(
        generatePatientDetails(singleMedicalPaOrder) as InfoGridData[],
      );
      setProviderData(
        generateProviderDetails(singleMedicalPaOrder) as InfoGridData[],
      );
    }
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    if (imageViewerModalOpen) {
      openModal("image-viewer-modal");
    }
  }, [imageViewerModalOpen]);

  const tabSwitchElements = useMemo(() => {
    if (!singleMedicalPaOrder) return [];
    const tabElements = determineFinalTabsListToBeShown(singleMedicalPaOrder);
    if (!isAutoNavigated) {
      navigate(tabElements[0].id);
      setIsAutoNavigated(true);
    }
    return tabElements;
  }, [singleMedicalPaOrder]);

  const onBackClick = () => {
    navigate(
      `/external-worklist/${getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE)}`,
    );
  };

  return (
    <div className="single-order-for-external--layout">
      {isLoading ? (
        <LoaderMessage message="Fetching Order Details..." />
      ) : error ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
          <div className="text-h16 font-bold text-red-500">Error</div>
          <div className="text-h14 text-gray-600">{error}</div>
          <button
            onClick={onBackClick}
            className="rounded-md bg-tertiaryBlue-4 px-4 py-2 text-white hover:bg-tertiaryBlue-5"
          >
            Go Back
          </button>
        </div>
      ) : (
        <>
          <div
            className="single-order-for-external--header"
            onClick={onBackClick}
          >
            <BackArrowBlue />
            Go Back
          </div>
          <div className="single-order-for-external--content">
            <div className="single-order-for-external--content--header">
              <Header
                authorizationId={"N/A"}
                status={singleMedicalPaOrder?.auth_on_file?.auth_status ?? ""}
                orderId={singleMedicalPaOrder?.order_id ?? ""}
                hideButton={true}
                orderData={singleMedicalPaOrder}
                patientMrn={singleMedicalPaOrder?.payload?.patient_id ?? ""}
                patientName={capitalizeString(
                  generateFullName(
                    singleMedicalPaOrder?.demographics?.first_name ?? "",
                    "",
                    singleMedicalPaOrder?.demographics?.last_name ?? "",
                  ),
                )}
                dateOfService={
                  singleMedicalPaOrder?.payload?.date_of_service ?? ""
                }
                location={singleMedicalPaOrder?.payload?.location ?? ""}
                providerName={rotateLastSegmentFirst(
                  singleMedicalPaOrder?.payload?.practitioner_name ?? "",
                )}
                insuranceName={
                  singleMedicalPaOrder?.coverage?.primary?.payer_name ?? ""
                }
              />
            </div>
            <div className="single-order-for-external--content--regimen-name mb-4 flex items-center justify-between rounded bg-[#FFFDE9] p-3 text-small font-bold">
              <div>
                Regimen Name :{" "}
                <span className="font-normal">
                  {singleMedicalPaOrder?.payload?.regimen_name ?? ""}
                </span>
              </div>
              {singleMedicalPaOrder?.auth_on_file?.screenshots
                ?.treatment_plan_path && (
                <span
                  className="view-tx-plan--btn text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer"
                  onClick={() => setImageViewerModalOpen(true)}
                >
                  View Tx Plan
                </span>
              )}
            </div>
            <div className="single-order-for-external--content--secondary">
              <div className="single-order-for-external--content--secondary__left">
                <div className="single-order-for-external--content--secondary__left--navigation">
                  {tabSwitchElements.map((sideNavElement) => (
                    <SingleTab
                      key={sideNavElement.id}
                      id={sideNavElement.id}
                      label={sideNavElement.label}
                    />
                  ))}
                </div>
                <div
                  className="single-order-for-external--content--secondary__left--outlet min-h-0"
                  style={{ overflow: "auto" }}
                >
                  <Outlet />
                </div>
              </div>
              <div className="single-order-for-external--content--secondary__right">
                <div className="single-order-for-external--content--secondary__right--patient-details">
                  Patient Details
                  <InfoGrid infoGridData={patientData} />
                </div>
                {checkIfInfoGridHeadersHouldBeShown(providerData ?? []) && (
                  <>
                    <div className="single-order-for-external--content--secondary__right--provider-details">
                      Provider Details
                      <InfoGrid infoGridData={providerData} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {isApproveEvbvModalOpen && (
        <ApproveEvbv
          onClose={() => setIsApproveEvbvModalOpen(false)}
          badgeList={financialStatusOptions.sort((a, b) =>
            a.id.localeCompare(b.id),
          )}
          selectedBadge={getCurrentStatusBadge}
          orderId={singleMedicalPaOrder?.id ?? ""}
          shouldNavigateToSubmission={checkIfSubmissionPageShouldBeShown()}
        />
      )}
      {imageViewerModalOpen && (
        <ImageviewerModal
          fileUrls={[]}
          onClose={() => {
            setImageViewerModalOpen(false);
          }}
          filePaths={
            singleMedicalPaOrder?.auth_on_file?.screenshots?.treatment_plan_path
              ? [
                  singleMedicalPaOrder?.auth_on_file?.screenshots
                    ?.treatment_plan_path,
                ]
              : []
          }
        >
          <ImageViewerTimestamp
            createdAt={
              singleMedicalPaOrder?.auth_on_file?.screenshots?.created_at
            }
          />
        </ImageviewerModal>
      )}
    </div>
  );
};

export default SingleOrderForExternalLayout;
